/**
 * TourFormModal - A comprehensive modal for creating and editing tours
 * Extracted from AdminDashboard.jsx for better maintainability
 */
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import RichTextEditor from '../ui/RichTextEditor';
import TourAvailabilityCalendar from './TourAvailabilityCalendar';
import { toast } from 'sonner';
import { 
  Loader2, X, Upload, Image, Check, Plus, Trash2, GripVertical,
  Map as MapIcon, Clock, Star, Eye, Zap, Bell, AlertCircle, TrendingUp, 
  Flame, Users, Calendar, Clipboard, CheckCircle, Link as LinkIcon, Tag,
  DollarSign, MapPin, Percent, Mail
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { adminAPI } from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function ImageUploadSection({ formData, setFormData }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const galleryCameraRef = useRef(null);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const base64Data = await base64Promise;

      // Upload to server
      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_data: base64Data }),
      });

      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      return `${API_URL}${data.url}`;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = async (e, isMainImage = false) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const url = await uploadImage(file);
          if (url) {
            if (isMainImage) {
              setFormData({ ...formData, image_url: url });
            } else {
              const currentImages = formData.images ? formData.images.trim() : '';
              const newImages = currentImages ? `${currentImages}\n${url}` : url;
              setFormData({ ...formData, images: newImages });
            }
            toast.success('Image uploaded successfully!');
          }
        }
        return;
      }
    }
  };

  const handleDrop = async (e, isMainImage = false) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer?.files;
    if (!files?.length) return;

    for (const file of files) {
      if (file.type.startsWith('image/')) {
        const url = await uploadImage(file);
        if (url) {
          if (isMainImage) {
            setFormData({ ...formData, image_url: url });
          } else {
            const currentImages = formData.images ? formData.images.trim() : '';
            const newImages = currentImages ? `${currentImages}\n${url}` : url;
            setFormData({ ...formData, images: newImages });
          }
          toast.success('Image uploaded successfully!');
        }
        break; // Only handle first image for main
      }
    }
  };

  const handleFileSelect = async (e, isMainImage = false) => {
    const files = e.target.files;
    if (!files?.length) return;

    for (const file of files) {
      if (file.type.startsWith('image/')) {
        const url = await uploadImage(file);
        if (url) {
          if (isMainImage) {
            setFormData({ ...formData, image_url: url });
          } else {
            const currentImages = formData.images ? formData.images.trim() : '';
            const newImages = currentImages ? `${currentImages}\n${url}` : url;
            setFormData({ ...formData, images: newImages });
          }
          toast.success('Image uploaded successfully!');
        }
      }
    }
    e.target.value = '';
  };

  const removeGalleryImage = (index) => {
    const images = formData.images.split('\n').filter(s => s.trim());
    images.splice(index, 1);
    setFormData({ ...formData, images: images.join('\n') });
  };

  const galleryImages = formData.images ? formData.images.split('\n').filter(s => s.trim()) : [];

  return (
    <div className="space-y-6">
      {/* Main Image */}
      <div>
        <Label className="text-base font-semibold">Main Image *</Label>
        <p className="text-sm text-muted-foreground mb-1">Take a photo, choose from gallery, or paste an image</p>
        <p className="text-xs text-blue-600 mb-2">📐 Recommended size: <strong>1200 x 800 pixels</strong> (3:2 ratio) • Max file size: 5MB • Formats: JPG, PNG, WebP</p>
        
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFileSelect(e, true)}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFileSelect(e, true)}
        />

        {/* Mobile-friendly buttons */}
        <div className="flex gap-2 mb-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-12"
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Take Photo
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-12"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Image className="h-5 w-5 mr-2" />
            Photo Library
          </Button>
        </div>

        {/* Drop/Paste zone */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-4 transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          onPaste={(e) => handlePaste(e, true)}
          onDrop={(e) => handleDrop(e, true)}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          tabIndex={0}
        >
          {formData.image_url ? (
            <div className="relative">
              <img 
                src={formData.image_url} 
                alt="Main preview" 
                className="w-full h-48 object-cover rounded-lg"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Image+Error'; }}
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, image_url: '' }); }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="text-center py-6">
              {uploading ? (
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              ) : (
                <>
                  <Clipboard className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Or paste image here (Ctrl+V)</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* URL Input fallback */}
        <div className="mt-2">
          <Input
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            placeholder="Or paste image URL here..."
            className="text-sm"
          />
        </div>
      </div>

      {/* Gallery Images */}
      <div>
        <Label className="text-base font-semibold">Gallery Images (up to 15)</Label>
        <p className="text-sm text-muted-foreground mb-1">Add multiple images for the tour detail slider</p>
        <p className="text-xs text-blue-600 mb-2">📐 Recommended size: <strong>1200 x 800 pixels</strong> (3:2 ratio) • Max file size: 5MB each</p>

        {/* Hidden file inputs for gallery */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFileSelect(e, false)}
        />
        <input
          ref={galleryCameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFileSelect(e, false)}
        />

        {/* Mobile-friendly buttons for gallery */}
        <div className="flex gap-2 mb-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-12"
            onClick={() => galleryCameraRef.current?.click()}
            disabled={uploading}
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Take Photo
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-12"
            onClick={() => galleryInputRef.current?.click()}
            disabled={uploading}
          >
            <Image className="h-5 w-5 mr-2" />
            Photo Library
          </Button>
        </div>

        {/* Paste/Drop Zone for Gallery */}
        <div
          className={`border-2 border-dashed rounded-lg p-4 mb-4 transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          onPaste={(e) => handlePaste(e, false)}
          onDrop={(e) => handleDrop(e, false)}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          tabIndex={0}
        >
          <div className="text-center py-4">
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
            ) : (
              <>
                <Clipboard className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                <p className="text-sm text-muted-foreground">Or paste images here (Ctrl+V)</p>
              </>
            )}
          </div>
        </div>

        {/* Gallery Preview Grid */}
        {galleryImages.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            {galleryImages.map((url, index) => (
              <div key={index} className="relative group aspect-video">
                <img 
                  src={url} 
                  alt={`Gallery ${index + 1}`} 
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Error'; }}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeGalleryImage(index)}
                >
                  <X className="h-3 w-3" />
                </Button>
                <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 rounded">
                  {index + 1}
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          {galleryImages.length} of 15 images added
        </p>

        {/* URL Input fallback for gallery */}
        <div className="mt-2">
          <Textarea
            value={formData.images}
            onChange={(e) => setFormData({ ...formData, images: e.target.value })}
            placeholder="Or paste image URLs here (one per line)..."
            className="min-h-[100px] font-mono text-xs"
          />
        </div>
      </div>

      {/* Tips */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">📱 Easy Upload Options</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Take Photo</strong> - Opens your camera directly</li>
          <li>• <strong>Photo Library</strong> - Select from your phone's gallery</li>
          <li>• <strong>Copy & Paste</strong> - Paste images from anywhere (Ctrl+V)</li>
          <li>• <strong>Drag & Drop</strong> - Drop images from your desktop</li>
        </ul>
      </div>
    </div>
  );
}

// Enhanced Tour Form Modal Component
function TourFormModal({ open, onClose, tour, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'adventure',
    location: '',
    price: '',
    duration: '',
    max_guests: '10',
    image_url: '',
    images: '',
    highlights: '',
    included: '',
    not_included: '',
    what_to_bring: '',
    meeting_point: '',
    fareharbor_url: '',
    is_active: true,
    // Custom Features - editable list
    custom_features: [
      { icon: 'shield', text: 'Best Price Guarantee', description: "We'll match any lower price" },
      { icon: 'clock', text: 'Free Cancellation', description: '24 hour refund policy' },
      { icon: 'star', text: 'Verified Reviews', description: 'Real traveler feedback' },
    ],
    // Category Badge Customization
    category_badge_text: '',
    category_badge_color: '#22c55e',
    hide_all_badges: false,
    // Custom Badges - fully editable badges with text, color, position
    custom_badges: [],
    // Section visibility
    show_highlights: true,
    show_description: true,
    show_included: true,
    show_what_to_bring: true,
    // Urgency & Social Proof
    show_popular_badge: true,
    show_selling_fast_badge: true,
    show_spots_left: true,
    show_live_viewers: true,
    show_recent_bookings: true,
    show_book_ahead_badge: true,
    show_booking_toast: true,
    show_limited_availability: true,
    // Discount
    discount_percentage: 0,
    discount_label: '',
    original_price: '',
    // Display settings
    show_category_badge: true,
    show_reviews: true,
    is_featured: false,
    featured_badge: 'Featured',
    // Cancellation Policy
    cancellation_text: 'Free Cancellation',
    show_cancellation_badge: true,
    // Dynamic Pricing
    enable_dynamic_pricing: false,
    weekend_price: null,
    peak_season_price: null,
    peak_season_dates: [],
    early_bird_discount: 0,
    last_minute_discount: 0,
    group_discount_threshold: 5,
    group_discount_percent: 10,
    // Email Template Customization
    confirmation_email_subject: '',
    confirmation_email_body: '',
    confirmation_sms_body: '',
  });

  useEffect(() => {
    if (tour) {
      setFormData({
        title: tour.title || '',
        description: tour.description || '',
        category: tour.category || 'adventure',
        location: tour.location || '',
        price: tour.price?.toString() || '',
        duration: tour.duration || '',
        max_guests: tour.max_guests?.toString() || '10',
        image_url: tour.image_url || '',
        images: tour.images?.join('\n') || '',
        highlights: tour.highlights?.join('\n') || '',
        included: tour.included?.join('\n') || '',
        not_included: tour.not_included?.join('\n') || '',
        what_to_bring: tour.what_to_bring?.join('\n') || '',
        meeting_point: tour.meeting_point || '',
        fareharbor_url: tour.fareharbor_url || '',
        is_active: tour.is_active !== false,
        // Custom Features
        custom_features: tour.custom_features?.length > 0 ? tour.custom_features : [
          { icon: 'shield', text: 'Best Price Guarantee', description: "We'll match any lower price" },
          { icon: 'clock', text: 'Free Cancellation', description: '24 hour refund policy' },
          { icon: 'star', text: 'Verified Reviews', description: 'Real traveler feedback' },
        ],
        // Category Badge Customization
        category_badge_text: tour.category_badge_text || '',
        category_badge_color: tour.category_badge_color || '#22c55e',
        hide_all_badges: tour.hide_all_badges === true,
        // Custom Badges
        custom_badges: tour.custom_badges || [],
        // Section visibility
        show_highlights: tour.show_highlights !== false,
        show_description: tour.show_description !== false,
        show_included: tour.show_included !== false,
        show_what_to_bring: tour.show_what_to_bring !== false,
        // Urgency & Social Proof
        show_popular_badge: tour.show_popular_badge !== false,
        show_selling_fast_badge: tour.show_selling_fast_badge !== false,
        show_spots_left: tour.show_spots_left !== false,
        show_live_viewers: tour.show_live_viewers !== false,
        show_recent_bookings: tour.show_recent_bookings !== false,
        show_book_ahead_badge: tour.show_book_ahead_badge !== false,
        show_booking_toast: tour.show_booking_toast !== false,
        show_limited_availability: tour.show_limited_availability !== false,
        // Discount
        discount_percentage: tour.discount_percentage || 0,
        discount_label: tour.discount_label || '',
        original_price: tour.original_price?.toString() || '',
        // Display settings
        show_category_badge: tour.show_category_badge !== false,
        show_reviews: tour.show_reviews !== false,
        is_featured: tour.is_featured === true,
        featured_badge: tour.featured_badge || 'Featured',
        featured_badge_color: tour.featured_badge_color || 'green',
        // Cancellation Policy
        cancellation_text: tour.cancellation_text || 'Free Cancellation',
        show_cancellation_badge: tour.show_cancellation_badge !== false,
        // Booking Options
        show_pickup_location: tour.show_pickup_location === true,
        // Dynamic Pricing
        enable_dynamic_pricing: tour.enable_dynamic_pricing || false,
        weekend_price: tour.weekend_price || null,
        peak_season_price: tour.peak_season_price || null,
        peak_season_dates: tour.peak_season_dates || [],
        early_bird_discount: tour.early_bird_discount || 0,
        last_minute_discount: tour.last_minute_discount || 0,
        group_discount_threshold: tour.group_discount_threshold || 5,
        group_discount_percent: tour.group_discount_percent || 10,
        // Email Template
        confirmation_email_subject: tour.confirmation_email_subject || '',
        confirmation_email_body: tour.confirmation_email_body || '',
        confirmation_sms_body: tour.confirmation_sms_body || '',
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'adventure',
        location: '',
        price: '',
        duration: '',
        max_guests: '10',
        image_url: '',
        images: '',
        highlights: '',
        included: '',
        not_included: '',
        what_to_bring: '',
        meeting_point: '',
        fareharbor_url: '',
        is_active: true,
        // Custom Features - default values
        custom_features: [
          { icon: 'shield', text: 'Best Price Guarantee', description: "We'll match any lower price" },
          { icon: 'clock', text: 'Free Cancellation', description: '24 hour refund policy' },
          { icon: 'star', text: 'Verified Reviews', description: 'Real traveler feedback' },
        ],
        // Category Badge Customization
        category_badge_text: '',
        category_badge_color: '#22c55e',
        hide_all_badges: false,
        // Custom Badges
        custom_badges: [],
        show_highlights: true,
        show_description: true,
        show_included: true,
        show_what_to_bring: true,
        // Urgency & Social Proof
        show_popular_badge: true,
        show_selling_fast_badge: true,
        show_spots_left: true,
        show_live_viewers: true,
        show_recent_bookings: true,
        show_book_ahead_badge: true,
        show_booking_toast: true,
        show_limited_availability: true,
        // Discount
        discount_percentage: 0,
        discount_label: '',
        original_price: '',
        // Display settings
        show_category_badge: true,
        show_reviews: true,
        is_featured: false,
        featured_badge: 'Featured',
        featured_badge_color: 'green',
        // Cancellation Policy
        cancellation_text: 'Free Cancellation',
        show_cancellation_badge: true,
        // Booking Options
        show_pickup_location: false,
      });
    }
    setActiveSection('basic');
  }, [tour, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Generate available dates for next 90 days
    const availableDates = [];
    for (let i = 1; i <= 90; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      availableDates.push(date.toISOString().split('T')[0]);
    }

    // Parse images from newline-separated text (with null safety)
    const imagesArray = (formData.images || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);

    // If no gallery images, use main image
    const finalImages = imagesArray.length > 0 ? imagesArray : [formData.image_url];

    const payload = {
      title: formData.title,
      description: formData.description,
      category: formData.category,
      location: formData.location,
      price: parseFloat(formData.price) || 0,
      duration: formData.duration,
      max_guests: parseInt(formData.max_guests) || 10,
      image_url: formData.image_url,
      images: finalImages,
      highlights: (formData.highlights || '').split('\n').map(s => s.trim()).filter(Boolean),
      included: (formData.included || '').split('\n').map(s => s.trim()).filter(Boolean),
      not_included: (formData.not_included || '').split('\n').map(s => s.trim()).filter(Boolean),
      what_to_bring: (formData.what_to_bring || '').split('\n').map(s => s.trim()).filter(Boolean),
      meeting_point: formData.meeting_point,
      fareharbor_url: formData.fareharbor_url,
      available_dates: availableDates,
      // Section visibility
      show_highlights: formData.show_highlights,
      show_description: formData.show_description,
      show_included: formData.show_included,
      show_what_to_bring: formData.show_what_to_bring,
      // Urgency & Social Proof
      show_popular_badge: formData.show_popular_badge,
      show_selling_fast_badge: formData.show_selling_fast_badge,
      show_spots_left: formData.show_spots_left,
      show_live_viewers: formData.show_live_viewers,
      show_recent_bookings: formData.show_recent_bookings,
      show_book_ahead_badge: formData.show_book_ahead_badge,
      show_booking_toast: formData.show_booking_toast,
      show_limited_availability: formData.show_limited_availability,
      // Custom Features
      custom_features: formData.custom_features || [],
      // Category Badge Customization
      category_badge_text: formData.category_badge_text || '',
      category_badge_color: formData.category_badge_color || '#22c55e',
      hide_all_badges: formData.hide_all_badges || false,
      // Custom Badges
      custom_badges: formData.custom_badges || [],
      // Discount
      discount_percentage: parseInt(formData.discount_percentage) || 0,
      discount_label: formData.discount_label || '',
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      // Display settings
      show_category_badge: formData.show_category_badge,
      show_reviews: formData.show_reviews,
      is_featured: formData.is_featured,
      featured_badge: formData.featured_badge || 'Featured',
      featured_badge_color: formData.featured_badge_color || 'green',
      // Cancellation Policy
      cancellation_text: formData.cancellation_text || 'Free Cancellation',
      show_cancellation_badge: formData.show_cancellation_badge !== false,
      // Booking options
      show_pickup_location: formData.show_pickup_location,
      // Email Template Customization
      confirmation_email_subject: formData.confirmation_email_subject || '',
      confirmation_email_body: formData.confirmation_email_body || '',
      confirmation_sms_body: formData.confirmation_sms_body || '',
    };

    // Add is_active for updates
    if (tour) {
      payload.is_active = formData.is_active;
    }

    try {
      if (tour) {
        await adminAPI.updateTour(tour.id, payload);
        toast.success('Tour updated successfully!');
      } else {
        await adminAPI.createTour(payload);
        toast.success('Tour created successfully!');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving tour:', error);
      toast.error(error.response?.data?.detail || 'Failed to save tour');
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: MapIcon },
    { id: 'details', label: 'Details', icon: Clock },
    { id: 'images', label: 'Images', icon: Image },
    { id: 'features', label: 'Features', icon: CheckCircle },
    { id: 'badges', label: 'Badges', icon: Tag },
    { id: 'booking', label: 'Booking', icon: LinkIcon },
    { id: 'availability', label: 'Availability', icon: Calendar },
    { id: 'discount', label: 'Discount', icon: DollarSign },
    { id: 'pricing', label: 'Dynamic Pricing', icon: TrendingUp },
    { id: 'urgency', label: 'Urgency', icon: Flame },
    { id: 'visibility', label: 'Visibility', icon: Eye },
    { id: 'email', label: 'Email Template', icon: Mail },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="border-b p-6 pb-4">
          <DialogTitle className="text-2xl">{tour ? 'Edit Tour' : 'Create New Tour'}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Section Navigation - Vertical Sidebar */}
          <div className="w-48 border-r bg-muted/30 p-3 overflow-y-auto">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-2">Sections</p>
            <div className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                    activeSection === section.id
                      ? 'bg-primary text-white shadow-sm'
                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <section.icon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{section.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Basic Info Section */}
          {activeSection === 'basic' && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Tour Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., El Yunque Rainforest Adventure"
                  className="mt-1"
                  required
                />
              </div>

              <div>
                <Label className="text-base font-semibold">Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your tour experience in detail..."
                  className="mt-1 min-h-[120px]"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-base font-semibold">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adventure">Adventure Tours</SelectItem>
                      <SelectItem value="marine">Marine Tours</SelectItem>
                      <SelectItem value="food">Food Tours</SelectItem>
                      <SelectItem value="night">Night Tours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-base font-semibold">Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., San Juan, Puerto Rico"
                    className="mt-1"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-base font-semibold">Price (USD) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="89.00"
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label className="text-base font-semibold">Duration *</Label>
                  <Input
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="e.g., 6 hours"
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label className="text-base font-semibold">Max Guests</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_guests}
                    onChange={(e) => setFormData({ ...formData, max_guests: e.target.value })}
                    placeholder="10"
                    className="mt-1"
                  />
                </div>
              </div>

              {tour && (
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Tour Status</Label>
                    <p className="text-sm text-muted-foreground">Enable or disable this tour listing</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              )}
            </div>
          )}

          {/* Details Section */}
          {activeSection === 'details' && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Meeting Point</Label>
                <Input
                  value={formData.meeting_point}
                  onChange={(e) => setFormData({ ...formData, meeting_point: e.target.value })}
                  placeholder="e.g., Hotel lobby pickup in San Juan"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-base font-semibold">Tour Highlights</Label>
                <p className="text-sm text-muted-foreground mb-1">Enter each highlight on a new line</p>
                <Textarea
                  value={formData.highlights}
                  onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                  placeholder="Waterfall swimming&#10;Guided nature walk&#10;Wildlife spotting&#10;Photo opportunities"
                  className="min-h-[100px] font-mono text-sm"
                />
              </div>

              <div>
                <Label className="text-base font-semibold">What's Included</Label>
                <p className="text-sm text-muted-foreground mb-1">Enter each item on a new line</p>
                <Textarea
                  value={formData.included}
                  onChange={(e) => setFormData({ ...formData, included: e.target.value })}
                  placeholder="Transportation&#10;Professional guide&#10;Snacks & water&#10;Safety equipment"
                  className="min-h-[100px] font-mono text-sm"
                />
              </div>

              <div>
                <Label className="text-base font-semibold">What&apos;s NOT Included</Label>
                <p className="text-sm text-muted-foreground mb-1">Enter each item on a new line</p>
                <Textarea
                  value={formData.not_included}
                  onChange={(e) => setFormData({ ...formData, not_included: e.target.value })}
                  placeholder="Lunch&#10;Tips&#10;Personal expenses"
                  className="min-h-[100px] font-mono text-sm"
                />
              </div>

              <div>
                <Label className="text-base font-semibold">What to Bring</Label>
                <p className="text-sm text-muted-foreground mb-1">Enter each item on a new line</p>
                <Textarea
                  value={formData.what_to_bring}
                  onChange={(e) => setFormData({ ...formData, what_to_bring: e.target.value })}
                  placeholder="Comfortable shoes&#10;Sunscreen&#10;Water bottle&#10;Camera"
                  className="min-h-[100px] font-mono text-sm"
                />
              </div>
            </div>
          )}

          {/* Images Section */}
          {activeSection === 'images' && (
            <ImageUploadSection 
              formData={formData} 
              setFormData={setFormData} 
            />
          )}

          {/* Features Section - Custom editable features */}
          {activeSection === 'features' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">✨ Tour Features</h4>
                <p className="text-sm text-blue-800">
                  Add custom features that appear on your tour card and detail page. 
                  Examples: "Free Cancellation", "Transport Included", "Lunch Provided"
                </p>
              </div>

              <div className="space-y-4">
                {(formData.custom_features || []).map((feature, index) => (
                  <div key={index} className="p-4 bg-white border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Feature {index + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const updated = [...formData.custom_features];
                          updated.splice(index, 1);
                          setFormData({ ...formData, custom_features: updated });
                        }}
                        className="text-red-500 hover:text-red-700 h-8 px-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-sm">Icon</Label>
                        <Select
                          value={feature.icon}
                          onValueChange={(value) => {
                            const updated = [...formData.custom_features];
                            updated[index].icon = value;
                            setFormData({ ...formData, custom_features: updated });
                          }}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="shield">🛡️ Shield (Guarantee)</SelectItem>
                            <SelectItem value="clock">⏰ Clock (Time/Cancellation)</SelectItem>
                            <SelectItem value="star">⭐ Star (Quality)</SelectItem>
                            <SelectItem value="check">✓ Check (Included)</SelectItem>
                            <SelectItem value="car">🚗 Car (Transport)</SelectItem>
                            <SelectItem value="utensils">🍽️ Utensils (Food)</SelectItem>
                            <SelectItem value="camera">📷 Camera (Photos)</SelectItem>
                            <SelectItem value="map">📍 Map (Location)</SelectItem>
                            <SelectItem value="users">👥 Users (Group)</SelectItem>
                            <SelectItem value="heart">❤️ Heart (Special)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-sm">Title</Label>
                        <Input
                          value={feature.text}
                          onChange={(e) => {
                            const updated = [...formData.custom_features];
                            updated[index].text = e.target.value;
                            setFormData({ ...formData, custom_features: updated });
                          }}
                          placeholder="e.g., Free Cancellation"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Description</Label>
                      <Input
                        value={feature.description}
                        onChange={(e) => {
                          const updated = [...formData.custom_features];
                          updated[index].description = e.target.value;
                          setFormData({ ...formData, custom_features: updated });
                        }}
                        placeholder="e.g., 24 hour refund policy"
                        className="mt-1"
                      />
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      custom_features: [
                        ...(formData.custom_features || []),
                        { icon: 'check', text: '', description: '' }
                      ]
                    });
                  }}
                  className="w-full gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Feature
                </Button>
              </div>

              {/* Quick Add Presets */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Quick Add Presets</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { icon: 'car', text: 'Transport Included', description: 'Hotel pickup & drop-off' },
                    { icon: 'utensils', text: 'Lunch Included', description: 'Traditional local meal' },
                    { icon: 'camera', text: 'Photos Included', description: 'Professional photos provided' },
                    { icon: 'users', text: 'Small Groups', description: 'Max 8 people per tour' },
                    { icon: 'clock', text: 'Flexible Schedule', description: 'Multiple departure times' },
                    { icon: 'shield', text: 'Licensed Guide', description: 'Certified tour guide' },
                  ].map((preset) => (
                    <Button
                      key={preset.text}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const exists = formData.custom_features?.some(f => f.text === preset.text);
                        if (!exists) {
                          setFormData({
                            ...formData,
                            custom_features: [...(formData.custom_features || []), preset]
                          });
                        }
                      }}
                      className="text-xs"
                    >
                      + {preset.text}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Cancellation Policy - Editable */}
              <div className="p-4 bg-green-50 rounded-lg space-y-4">
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">✓ Cancellation Policy Badge</h4>
                  <p className="text-sm text-green-800 mb-4">
                    Customize the cancellation policy text shown on tour cards (e.g., "Free Cancellation", "24hr Refund", "No Refunds")
                  </p>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <Label className="text-sm font-medium">Show Cancellation Badge</Label>
                    <p className="text-xs text-muted-foreground">Display cancellation policy on tour cards</p>
                  </div>
                  <Switch
                    checked={formData.show_cancellation_badge !== false}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_cancellation_badge: checked })}
                    data-testid="show-cancellation-badge"
                  />
                </div>

                {formData.show_cancellation_badge !== false && (
                  <div>
                    <Label className="text-sm font-medium">Cancellation Policy Text</Label>
                    <Input
                      value={formData.cancellation_text || ''}
                      onChange={(e) => setFormData({ ...formData, cancellation_text: e.target.value })}
                      placeholder="Free Cancellation"
                      className="mt-1"
                      data-testid="cancellation-text-input"
                    />
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['Free Cancellation', '24hr Refund', '48hr Cancellation', 'Non-Refundable', 'Flexible Booking', 'Easy Reschedule'].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setFormData({ ...formData, cancellation_text: preset })}
                          className={`px-2 py-1 text-xs rounded border hover:bg-green-100 ${
                            formData.cancellation_text === preset ? 'bg-green-200 border-green-400' : ''
                          }`}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                    
                    {/* Preview */}
                    <div className="mt-3 p-3 bg-white rounded-lg border">
                      <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                      <div className="flex items-center gap-1 text-sm text-green-700 bg-green-100 px-3 py-1.5 rounded-full font-medium w-fit">
                        <Check className="h-3.5 w-3.5" />
                        <span>{formData.cancellation_text || 'Free Cancellation'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Badges Section - Category badge customization */}
          {activeSection === 'badges' && (
            <div className="space-y-6">
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">Badge Customization</h4>
                <p className="text-sm text-purple-800">
                  Customize or hide the badges that appear on tour cards. 
                  Create custom badges with your own text, color, and position.
                </p>
              </div>

              {/* Hide All Badges */}
              <div className="flex items-center justify-between p-4 bg-white border-2 border-red-200 rounded-lg">
                <div>
                  <Label className="text-base font-semibold text-red-700">Hide All Badges</Label>
                  <p className="text-sm text-muted-foreground">
                    Turn this on to hide ALL badges on the tour card (category, popular, custom, etc.)
                  </p>
                </div>
                <Switch
                  checked={formData.hide_all_badges}
                  onCheckedChange={(checked) => setFormData({ ...formData, hide_all_badges: checked })}
                  data-testid="hide-all-badges"
                />
              </div>

              {!formData.hide_all_badges && (
                <>
                  {/* Category Badge Customization */}
                  <div className="p-4 bg-white border rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-semibold">Show Category Badge</Label>
                        <p className="text-sm text-muted-foreground">
                          Display the category badge on tour cards (e.g., "Adventure", "Marine")
                        </p>
                      </div>
                      <Switch
                        checked={formData.show_category_badge}
                        onCheckedChange={(checked) => setFormData({ ...formData, show_category_badge: checked })}
                      />
                    </div>

                    {formData.show_category_badge && (
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm font-medium">Custom Badge Text (Optional)</Label>
                          <p className="text-xs text-muted-foreground mb-2">
                            Leave empty to use the default category name. Enter custom text to override.
                          </p>
                          <Input
                            value={formData.category_badge_text || ''}
                            onChange={(e) => setFormData({ ...formData, category_badge_text: e.target.value })}
                            placeholder={`Default: ${formData.category ? formData.category.charAt(0).toUpperCase() + formData.category.slice(1) : 'Adventure'}`}
                            className="mt-1"
                          />
                          <div className="flex flex-wrap gap-2 mt-2">
                            {['Best Seller', 'Top Rated', 'New!', 'Limited', 'VIP', 'Family Friendly', 'Eco Tour', 'Luxury'].map((preset) => (
                              <button
                                key={preset}
                                type="button"
                                onClick={() => setFormData({ ...formData, category_badge_text: preset })}
                                className={`px-2 py-1 text-xs rounded border hover:bg-gray-100 ${
                                  formData.category_badge_text === preset ? 'bg-gray-200 border-gray-400' : ''
                                }`}
                              >
                                {preset}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium">Badge Color</Label>
                          <div className="flex items-center gap-3 mt-2">
                            <input
                              type="color"
                              value={formData.category_badge_color || '#22c55e'}
                              onChange={(e) => setFormData({ ...formData, category_badge_color: e.target.value })}
                              className="w-10 h-10 rounded cursor-pointer border"
                            />
                            <Input
                              value={formData.category_badge_color || '#22c55e'}
                              onChange={(e) => setFormData({ ...formData, category_badge_color: e.target.value })}
                              placeholder="#22c55e"
                              className="w-32"
                            />
                            <div className="flex gap-1">
                              {['#22c55e', '#ef4444', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#64748b'].map((color) => (
                                <button
                                  key={color}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, category_badge_color: color })}
                                  className={`w-6 h-6 rounded-full border-2 ${formData.category_badge_color === color ? 'border-gray-800' : 'border-transparent'}`}
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        {/* Preview */}
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                          <Badge 
                            className="text-white border-0"
                            style={{ backgroundColor: formData.category_badge_color || '#22c55e' }}
                          >
                            {formData.category_badge_text || (formData.category ? formData.category.charAt(0).toUpperCase() + formData.category.slice(1) : 'Adventure')}
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Custom Badges Section */}
                  <div className="p-4 bg-white border rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-semibold">Custom Badges</Label>
                        <p className="text-sm text-muted-foreground">
                          Add fully customizable badges with custom text, color, and position
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            custom_badges: [
                              ...(formData.custom_badges || []),
                              { text: 'New Badge', color: '#ef4444', position: 'top-right' }
                            ]
                          });
                        }}
                        className="gap-1"
                      >
                        <Plus className="h-4 w-4" />
                        Add Badge
                      </Button>
                    </div>

                    {(formData.custom_badges || []).length > 0 ? (
                      <div className="space-y-3">
                        {(formData.custom_badges || []).map((badge, index) => (
                          <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-600">Custom Badge {index + 1}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = [...(formData.custom_badges || [])];
                                  updated.splice(index, 1);
                                  setFormData({ ...formData, custom_badges: updated });
                                }}
                                className="text-red-500 hover:text-red-700 h-8 px-2"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-3">
                              <div className="col-span-2">
                                <Label className="text-sm">Badge Text</Label>
                                <Input
                                  value={badge.text}
                                  onChange={(e) => {
                                    const updated = [...(formData.custom_badges || [])];
                                    updated[index].text = e.target.value;
                                    setFormData({ ...formData, custom_badges: updated });
                                  }}
                                  placeholder="e.g., Best Value"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-sm">Position</Label>
                                <Select
                                  value={badge.position}
                                  onValueChange={(value) => {
                                    const updated = [...(formData.custom_badges || [])];
                                    updated[index].position = value;
                                    setFormData({ ...formData, custom_badges: updated });
                                  }}
                                >
                                  <SelectTrigger className="mt-1">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="top-left">Top Left</SelectItem>
                                    <SelectItem value="top-right">Top Right</SelectItem>
                                    <SelectItem value="bottom-left">Bottom Left</SelectItem>
                                    <SelectItem value="bottom-right">Bottom Right</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-sm">Badge Color</Label>
                              <div className="flex items-center gap-3 mt-1">
                                <input
                                  type="color"
                                  value={badge.color || '#ef4444'}
                                  onChange={(e) => {
                                    const updated = [...(formData.custom_badges || [])];
                                    updated[index].color = e.target.value;
                                    setFormData({ ...formData, custom_badges: updated });
                                  }}
                                  className="w-8 h-8 rounded cursor-pointer border"
                                />
                                <Input
                                  value={badge.color || '#ef4444'}
                                  onChange={(e) => {
                                    const updated = [...(formData.custom_badges || [])];
                                    updated[index].color = e.target.value;
                                    setFormData({ ...formData, custom_badges: updated });
                                  }}
                                  placeholder="#ef4444"
                                  className="w-28"
                                />
                                <div className="flex gap-1">
                                  {['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#000000'].map((color) => (
                                    <button
                                      key={color}
                                      type="button"
                                      onClick={() => {
                                        const updated = [...(formData.custom_badges || [])];
                                        updated[index].color = color;
                                        setFormData({ ...formData, custom_badges: updated });
                                      }}
                                      className={`w-5 h-5 rounded-full border-2 ${badge.color === color ? 'border-gray-800' : 'border-gray-300'}`}
                                      style={{ backgroundColor: color }}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            {/* Badge Preview */}
                            <div className="p-2 bg-white rounded border">
                              <p className="text-xs text-muted-foreground mb-1">Preview:</p>
                              <Badge 
                                className="text-white text-xs border-0"
                                style={{ backgroundColor: badge.color || '#ef4444' }}
                              >
                                {badge.text || 'Badge Text'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4 text-sm">
                        No custom badges added. Click "Add Badge" to create one.
                      </p>
                    )}

                    {/* Quick Presets */}
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Quick Add Presets</Label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { text: 'Best Seller', color: '#ef4444', position: 'top-right' },
                          { text: 'New!', color: '#3b82f6', position: 'top-right' },
                          { text: 'Limited Time', color: '#f59e0b', position: 'top-right' },
                          { text: 'VIP', color: '#8b5cf6', position: 'top-left' },
                          { text: 'Trending', color: '#ec4899', position: 'top-right' },
                          { text: 'Editor Pick', color: '#14b8a6', position: 'top-left' },
                        ].map((preset) => (
                          <Button
                            key={preset.text}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const exists = (formData.custom_badges || []).some(b => b.text === preset.text);
                              if (!exists) {
                                setFormData({
                                  ...formData,
                                  custom_badges: [...(formData.custom_badges || []), preset]
                                });
                              }
                            }}
                            className="text-xs"
                          >
                            + {preset.text}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Popularity Badge Toggle */}
                  <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                    <div>
                      <Label className="text-base font-semibold">Show Popular Badge</Label>
                      <p className="text-sm text-muted-foreground">
                        Display "Popular" badge for tours with high booking rates
                      </p>
                    </div>
                    <Switch
                      checked={formData.show_popular_badge}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_popular_badge: checked })}
                    />
                  </div>

                  {/* Selling Fast Badge Toggle */}
                  <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                    <div>
                      <Label className="text-base font-semibold">Show Selling Fast Badge</Label>
                      <p className="text-sm text-muted-foreground">
                        Display "Selling Fast" indicator for high-demand tours
                      </p>
                    </div>
                    <Switch
                      checked={formData.show_selling_fast_badge}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_selling_fast_badge: checked })}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Booking Section */}
          {activeSection === 'booking' && (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 rounded-lg mb-4">
                <h4 className="font-semibold text-yellow-900 mb-2">🔗 FareHarbor Integration</h4>
                <p className="text-sm text-yellow-800">
                  If you use FareHarbor for bookings, add your booking link below. 
                  Customers will be redirected to FareHarbor when they click "Book Now".
                </p>
              </div>

              <div>
                <Label className="text-base font-semibold">FareHarbor Booking URL</Label>
                <p className="text-sm text-muted-foreground mb-1">Leave empty to use the built-in booking system</p>
                <Input
                  value={formData.fareharbor_url}
                  onChange={(e) => setFormData({ ...formData, fareharbor_url: e.target.value })}
                  placeholder="https://fareharbor.com/embeds/book/your-company/items/12345/"
                  className="mt-1"
                />
              </div>

              {formData.fareharbor_url && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800">
                    ✓ FareHarbor link configured. Customers will be redirected when booking.
                  </p>
                </div>
              )}

              {!formData.fareharbor_url && (
                <div className="p-4 bg-blue-50 rounded-lg space-y-4">
                  <p className="text-sm text-blue-800">
                    ✓ Using built-in booking system. Configure options below:
                  </p>
                  
                  {/* Pickup Location Toggle */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div>
                      <Label className="text-base font-semibold">Show Pickup Location Option</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow customers to select hotel pickup during booking
                      </p>
                    </div>
                    <Switch
                      checked={formData.show_pickup_location || false}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_pickup_location: checked })}
                      data-testid="show-pickup-location"
                    />
                  </div>
                  
                  {/* Available Times */}
                  <div>
                    <Label className="text-base font-semibold">Available Times</Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Enter available tour times (comma separated, 24h format)
                    </p>
                    <Input
                      value={(formData.available_times || ['09:00', '10:00', '11:00', '14:00', '15:00']).join(', ')}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        available_times: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                      })}
                      placeholder="09:00, 10:00, 14:00, 15:00"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Availability Section */}
          {activeSection === 'availability' && tour && (
            <TourAvailabilitySection tourId={tour.id} tourTitle={tour.title} />
          )}
          
          {activeSection === 'availability' && !tour && (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Save the tour first</p>
              <p className="text-sm">Availability can only be managed after the tour is created.</p>
            </div>
          )}

          {/* Discount Section */}
          {activeSection === 'discount' && (
            <div className="space-y-6">
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">💰 Discount Settings</h4>
                <p className="text-sm text-purple-800">
                  Add special offers and discounts to attract more bookings. The discount badge will be prominently displayed on tour cards.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-base font-semibold">Discount Percentage</Label>
                  <p className="text-sm text-muted-foreground mb-1">Set to 0 for no discount</p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.discount_percentage}
                      onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                      placeholder="0"
                      className="w-24"
                    />
                    <span className="text-lg font-semibold">%</span>
                  </div>
                </div>

                <div>
                  <Label className="text-base font-semibold">Original Price</Label>
                  <p className="text-sm text-muted-foreground mb-1">Price before discount (optional)</p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.original_price}
                      onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                      placeholder="0.00"
                      className="w-32"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold">Discount Label</Label>
                <p className="text-sm text-muted-foreground mb-1">Custom label for the promotion (e.g., "Early Bird", "Holiday Special")</p>
                <Input
                  value={formData.discount_label}
                  onChange={(e) => setFormData({ ...formData, discount_label: e.target.value })}
                  placeholder="e.g., Early Bird Special, Summer Sale, Holiday Deal"
                  className="mt-1"
                />
              </div>

              {(formData.discount_percentage > 0 || formData.discount_label) && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h5 className="font-semibold text-green-900 mb-2">Preview:</h5>
                  <div className="flex flex-wrap gap-2">
                    {formData.discount_percentage > 0 && (
                      <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {formData.discount_percentage}% OFF
                      </span>
                    )}
                    {formData.discount_label && (
                      <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {formData.discount_label}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Dynamic Pricing Section */}
          {activeSection === 'pricing' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">💰 Dynamic Pricing</h4>
                <p className="text-sm text-blue-800">
                  Set different prices based on demand, seasonality, and booking behavior. Maximize revenue with smart pricing rules.
                </p>
              </div>

              {/* Enable Dynamic Pricing */}
              <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                <div>
                  <Label className="text-base font-semibold">Enable Dynamic Pricing</Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, prices will adjust based on the rules below
                  </p>
                </div>
                <Switch
                  checked={formData.enable_dynamic_pricing || false}
                  onCheckedChange={(checked) => setFormData({ ...formData, enable_dynamic_pricing: checked })}
                  data-testid="enable-dynamic-pricing"
                />
              </div>

              {formData.enable_dynamic_pricing && (
                <>
                  {/* Weekend Pricing */}
                  <Card className="p-4">
                    <h5 className="font-semibold mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      Weekend Pricing
                    </h5>
                    <p className="text-sm text-muted-foreground mb-3">
                      Set a different price for Friday, Saturday, and Sunday bookings
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Base Price</Label>
                        <div className="text-2xl font-bold text-primary">${formData.price || 0}</div>
                      </div>
                      <div>
                        <Label>Weekend Price</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.weekend_price || ''}
                          onChange={(e) => setFormData({ ...formData, weekend_price: parseFloat(e.target.value) || null })}
                          placeholder="e.g., 89.00"
                          data-testid="weekend-price"
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Peak Season Pricing */}
                  <Card className="p-4">
                    <h5 className="font-semibold mb-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-orange-500" />
                      Peak Season Pricing
                    </h5>
                    <p className="text-sm text-muted-foreground mb-3">
                      Higher price during peak tourist seasons (holidays, summer, etc.)
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label>Peak Season Price</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.peak_season_price || ''}
                          onChange={(e) => setFormData({ ...formData, peak_season_price: parseFloat(e.target.value) || null })}
                          placeholder="e.g., 99.00"
                          data-testid="peak-season-price"
                        />
                      </div>
                      <div>
                        <Label>Peak Season Dates (comma-separated)</Label>
                        <Input
                          value={(formData.peak_season_dates || []).join(', ')}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            peak_season_dates: e.target.value.split(',').map(d => d.trim()).filter(d => d)
                          })}
                          placeholder="e.g., Dec 15 - Jan 5, Jul 1 - Aug 31"
                          data-testid="peak-season-dates"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Enter date ranges separated by commas
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Early Bird Discount */}
                  <Card className="p-4">
                    <h5 className="font-semibold mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-green-500" />
                      Early Bird Discount
                    </h5>
                    <p className="text-sm text-muted-foreground mb-3">
                      Reward customers who book in advance (30+ days before tour date)
                    </p>
                    <div>
                      <Label>Discount Percentage</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="50"
                          value={formData.early_bird_discount || 0}
                          onChange={(e) => setFormData({ ...formData, early_bird_discount: parseInt(e.target.value) || 0 })}
                          className="w-24"
                          data-testid="early-bird-discount"
                        />
                        <span className="text-muted-foreground">%</span>
                        <span className="text-sm text-green-600 ml-2">
                          = ${((formData.price || 0) * (1 - (formData.early_bird_discount || 0) / 100)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </Card>

                  {/* Last Minute Discount */}
                  <Card className="p-4">
                    <h5 className="font-semibold mb-3 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-red-500" />
                      Last Minute Discount
                    </h5>
                    <p className="text-sm text-muted-foreground mb-3">
                      Fill empty spots with discounts for bookings within 48 hours
                    </p>
                    <div>
                      <Label>Discount Percentage</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="50"
                          value={formData.last_minute_discount || 0}
                          onChange={(e) => setFormData({ ...formData, last_minute_discount: parseInt(e.target.value) || 0 })}
                          className="w-24"
                          data-testid="last-minute-discount"
                        />
                        <span className="text-muted-foreground">%</span>
                        <span className="text-sm text-red-600 ml-2">
                          = ${((formData.price || 0) * (1 - (formData.last_minute_discount || 0) / 100)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </Card>

                  {/* Group Discount */}
                  <Card className="p-4">
                    <h5 className="font-semibold mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4 text-purple-500" />
                      Group Discount
                    </h5>
                    <p className="text-sm text-muted-foreground mb-3">
                      Offer discounts for larger groups to encourage bookings
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Minimum Guests</Label>
                        <Input
                          type="number"
                          min="2"
                          max="20"
                          value={formData.group_discount_threshold || 5}
                          onChange={(e) => setFormData({ ...formData, group_discount_threshold: parseInt(e.target.value) || 5 })}
                          data-testid="group-discount-threshold"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Discount applies for this many guests or more</p>
                      </div>
                      <div>
                        <Label>Discount Percentage</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="30"
                            value={formData.group_discount_percent || 10}
                            onChange={(e) => setFormData({ ...formData, group_discount_percent: parseInt(e.target.value) || 10 })}
                            className="w-24"
                            data-testid="group-discount-percent"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </div>
          )}

          {/* Urgency Section */}
          {activeSection === 'urgency' && (
            <div className="space-y-6">
              <div className="p-4 bg-orange-50 rounded-lg">
                <h4 className="font-semibold text-orange-900 mb-2">🔥 Urgency & Social Proof</h4>
                <p className="text-sm text-orange-800">
                  Control which urgency elements appear on this tour's card and detail page. These elements help boost conversions.
                </p>
              </div>

              {/* Tour Card Badges */}
              <div>
                <h5 className="font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wide">Tour Card Badges</h5>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Flame className="h-4 w-4 text-orange-500" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">"Popular" Badge</Label>
                        <p className="text-xs text-muted-foreground">Shows when tour has high rating</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.show_popular_badge}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_popular_badge: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-red-500" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">"Selling Fast" Badge</Label>
                        <p className="text-xs text-muted-foreground">Shows when demand is high</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.show_selling_fast_badge}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_selling_fast_badge: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Users className="h-4 w-4 text-amber-600" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">"Only X Spots Left"</Label>
                        <p className="text-xs text-muted-foreground">Scarcity indicator on cards</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.show_spots_left}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_spots_left: checked })}
                    />
                  </div>
                </div>
              </div>

              {/* Tour Detail Page */}
              <div>
                <h5 className="font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wide">Tour Detail Page</h5>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Eye className="h-4 w-4 text-blue-500" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Live Viewers Counter</Label>
                        <p className="text-xs text-muted-foreground">"X people viewing now"</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.show_live_viewers}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_live_viewers: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Zap className="h-4 w-4 text-purple-500" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Recent Bookings</Label>
                        <p className="text-xs text-muted-foreground">"X bookings in 24 hours"</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.show_recent_bookings}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_recent_bookings: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-orange-100 to-red-100 rounded-lg flex items-center justify-center">
                        <Clock className="h-4 w-4 text-orange-500" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">"Book Ahead" Badge</Label>
                        <p className="text-xs text-muted-foreground">Urgency badge near Book Now</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.show_book_ahead_badge}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_book_ahead_badge: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <Bell className="h-4 w-4 text-green-500" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Booking Toast</Label>
                        <p className="text-xs text-muted-foreground">"Sarah from NYC booked..."</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.show_booking_toast}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_booking_toast: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">"Limited Availability" CTA</Label>
                        <p className="text-xs text-muted-foreground">Badge on booking button</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.show_limited_availability}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_limited_availability: checked })}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Visibility Section */}
          {activeSection === 'visibility' && (
            <div className="space-y-6">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Control which sections are visible on the tour detail page. Toggle off to hide a section for this tour.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Show Category Badge</Label>
                    <p className="text-sm text-muted-foreground">Display the category badge on tour cards (e.g., "Adventure", "Marine")</p>
                  </div>
                  <Switch
                    checked={formData.show_category_badge}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_category_badge: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Show Highlights</Label>
                    <p className="text-sm text-muted-foreground">Display the highlights section on the tour page</p>
                  </div>
                  <Switch
                    checked={formData.show_highlights}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_highlights: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Show Description</Label>
                    <p className="text-sm text-muted-foreground">Display the "About This Tour" section</p>
                  </div>
                  <Switch
                    checked={formData.show_description}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_description: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Show What&apos;s Included</Label>
                    <p className="text-sm text-muted-foreground">Display the &quot;What&apos;s Included&quot; section</p>
                  </div>
                  <Switch
                    checked={formData.show_included}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_included: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Show What to Bring</Label>
                    <p className="text-sm text-muted-foreground">Display the &quot;What to Bring&quot; section</p>
                  </div>
                  <Switch
                    checked={formData.show_what_to_bring}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_what_to_bring: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Show Reviews Section</Label>
                    <p className="text-sm text-muted-foreground">Display customer reviews on the tour page</p>
                  </div>
                  <Switch
                    checked={formData.show_reviews}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_reviews: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg">
                  <div>
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-500" />
                      Featured Tour
                    </Label>
                    <p className="text-sm text-muted-foreground">Display in the featured carousel on homepage</p>
                  </div>
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                    data-testid="toggle-is-featured"
                  />
                </div>

                {/* Featured Badge Selector - Only show when Featured is enabled */}
                {formData.is_featured && (
                  <div className="p-4 bg-amber-50/50 border border-amber-200 rounded-lg space-y-4">
                    <div>
                      <Label className="font-semibold">Badge Text</Label>
                      <p className="text-xs text-muted-foreground mb-2">Type your own badge text or select a preset</p>
                      <Input
                        value={formData.featured_badge || ''}
                        onChange={(e) => setFormData({ ...formData, featured_badge: e.target.value })}
                        placeholder="e.g., Must Do!, Popular, Best Seller"
                        className="mb-2"
                      />
                      <div className="flex flex-wrap gap-2">
                        {['Must Do!', 'Popular', 'Best Seller', 'Top Rated', 'New', 'Limited', 'Exclusive', 'Featured'].map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => setFormData({ ...formData, featured_badge: preset })}
                            className={`px-2 py-1 text-xs rounded border hover:bg-gray-100 ${
                              formData.featured_badge === preset ? 'bg-gray-200 border-gray-400' : ''
                            }`}
                          >
                            {preset}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label className="font-semibold">Badge Color</Label>
                      <p className="text-xs text-muted-foreground mb-2">Choose the badge background color</p>
                      <div className="flex gap-3">
                        {[
                          { value: 'white', label: 'White', bg: 'bg-white border-2 border-gray-300', text: 'text-gray-800' },
                          { value: 'red', label: 'Red', bg: 'bg-red-500', text: 'text-white' },
                          { value: 'green', label: 'Green', bg: 'bg-green-500', text: 'text-white' },
                          { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-400', text: 'text-gray-900' },
                        ].map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, featured_badge_color: color.value })}
                            className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
                              formData.featured_badge_color === color.value 
                                ? 'border-primary ring-2 ring-primary/30' 
                                : 'border-transparent hover:border-gray-300'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full ${color.bg}`} />
                            <span className="text-xs">{color.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Preview */}
                    <div>
                      <Label className="font-semibold">Preview</Label>
                      <div className="mt-2 p-4 bg-gray-800 rounded-lg inline-block">
                        <span className={`px-3 py-1.5 rounded-full text-sm font-bold ${
                          formData.featured_badge_color === 'white' ? 'bg-white text-gray-800 border border-gray-300' :
                          formData.featured_badge_color === 'red' ? 'bg-red-500 text-white' :
                          formData.featured_badge_color === 'green' ? 'bg-green-500 text-white' :
                          formData.featured_badge_color === 'yellow' ? 'bg-yellow-400 text-gray-900' :
                          'bg-primary text-white'
                        }`}>
                          {formData.featured_badge || 'Featured'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {tour && (
                <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Tour Active</Label>
                    <p className="text-sm text-muted-foreground">Inactive tours won't appear in listings</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              )}
            </div>
          )}

          {/* Email Template Section */}
          {activeSection === 'email' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Booking Confirmation Email Template
                </h4>
                <p className="text-sm text-blue-800">
                  Customize the booking confirmation email sent to customers when they book this tour. 
                  Leave fields empty to use the default system template.
                </p>
              </div>

              {/* Use Default Toggle */}
              <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
                <div>
                  <Label className="text-base font-semibold">Use Custom Email Template</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable to override the default booking confirmation email for this tour
                  </p>
                </div>
                <Switch
                  checked={!!(formData.confirmation_email_subject || formData.confirmation_email_body)}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      setFormData({ 
                        ...formData, 
                        confirmation_email_subject: '', 
                        confirmation_email_body: '',
                        confirmation_sms_body: ''
                      });
                    }
                  }}
                  data-testid="toggle-custom-email"
                />
              </div>

              {/* Email Subject */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Email Subject</Label>
                <Input
                  value={formData.confirmation_email_subject}
                  onChange={(e) => setFormData({ ...formData, confirmation_email_subject: e.target.value })}
                  placeholder="e.g., Your Adventure Awaits! Booking Confirmed for {{tour_title}}"
                  data-testid="email-subject-input"
                />
                <p className="text-xs text-muted-foreground">
                  Available placeholders: {"{{customer_name}}"}, {"{{tour_title}}"}, {"{{booking_date}}"}, {"{{booking_time}}"}, {"{{reference_number}}"}
                </p>
              </div>

              {/* Email Body */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Email Body (HTML Supported)</Label>
                <Textarea
                  value={formData.confirmation_email_body}
                  onChange={(e) => setFormData({ ...formData, confirmation_email_body: e.target.value })}
                  placeholder={`Dear {{customer_name}},

Thank you for booking {{tour_title}}!

Your adventure is confirmed for {{booking_date}} at {{booking_time}}.
Reference Number: {{reference_number}}

We can't wait to see you!

Best regards,
Tour Finder Team`}
                  className="min-h-[200px] font-mono text-sm"
                  data-testid="email-body-input"
                />
                <p className="text-xs text-muted-foreground">
                  You can use HTML tags for formatting. Available placeholders: {"{{customer_name}}"}, {"{{tour_title}}"}, {"{{booking_date}}"}, {"{{booking_time}}"}, {"{{reference_number}}"}, {"{{guests}}"}, {"{{total_price}}"}
                </p>
              </div>

              {/* Quick Insert Buttons */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Quick Insert Placeholders</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Customer Name', value: '{{customer_name}}' },
                    { label: 'Tour Title', value: '{{tour_title}}' },
                    { label: 'Date', value: '{{booking_date}}' },
                    { label: 'Time', value: '{{booking_time}}' },
                    { label: 'Reference', value: '{{reference_number}}' },
                    { label: 'Guests', value: '{{guests}}' },
                    { label: 'Total', value: '{{total_price}}' },
                  ].map((placeholder) => (
                    <Button
                      key={placeholder.label}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const currentBody = formData.confirmation_email_body || '';
                        setFormData({ ...formData, confirmation_email_body: currentBody + placeholder.value });
                      }}
                      className="text-xs"
                    >
                      + {placeholder.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* SMS Template */}
              <div className="border-t pt-6 space-y-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">SMS Confirmation Template</h4>
                  <p className="text-sm text-green-800">
                    Customize the SMS sent to customers. Keep it short (160 characters recommended).
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-base font-semibold">SMS Message</Label>
                    <span className={`text-xs ${(formData.confirmation_sms_body?.length || 0) > 160 ? 'text-red-500' : 'text-muted-foreground'}`}>
                      {formData.confirmation_sms_body?.length || 0}/160
                    </span>
                  </div>
                  <Textarea
                    value={formData.confirmation_sms_body}
                    onChange={(e) => setFormData({ ...formData, confirmation_sms_body: e.target.value })}
                    placeholder={`🎉 Booking Confirmed! {{tour_title}} on {{booking_date}} at {{booking_time}}. Ref: {{reference_number}}. See you there! - Tour Finder`}
                    className="min-h-[100px] font-mono text-sm"
                    data-testid="sms-body-input"
                  />
                </div>
              </div>

              {/* Preview Section */}
              <div className="border-t pt-6">
                <Label className="text-base font-semibold mb-4 block">Preview</Label>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b">
                    <p className="text-sm font-medium">
                      Subject: {formData.confirmation_email_subject || '(Using default subject)'}
                    </p>
                  </div>
                  <div className="p-4 bg-white">
                    <div className="prose prose-sm max-w-none">
                      {formData.confirmation_email_body ? (
                        <div dangerouslySetInnerHTML={{ __html: formData.confirmation_email_body
                          .replace(/\{\{customer_name\}\}/g, 'John Smith')
                          .replace(/\{\{tour_title\}\}/g, formData.title || 'Tour Name')
                          .replace(/\{\{booking_date\}\}/g, 'Jan 15, 2026')
                          .replace(/\{\{booking_time\}\}/g, '9:00 AM')
                          .replace(/\{\{reference_number\}\}/g, 'XYZ12')
                          .replace(/\{\{guests\}\}/g, '2')
                          .replace(/\{\{total_price\}\}/g, '$' + (formData.price || '99'))
                          .replace(/\n/g, '<br>')
                        }} />
                      ) : (
                        <p className="text-muted-foreground italic">
                          No custom email body set. The default system template will be used.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="bg-primary"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              tour ? 'Update Tour' : 'Create Tour'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Simple wrapper for TourAvailabilityCalendar
function TourAvailabilitySection({ tourId, tourTitle }) {
  return <TourAvailabilityCalendar tourId={tourId} tourTitle={tourTitle} />;
}

export default TourFormModal;
export { ImageUploadSection, TourAvailabilitySection };
